import { useEffect, useState, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, Phone, ArrowUpDown, Eye, Users, Trophy, Target } from "lucide-react";

const PRIORITY_META = {
  A: { label: "Priority A · High Match", color: "#10B981", desc: "80–100% — strong recommendation" },
  B: { label: "Priority B · Medium Match", color: "#F59E0B", desc: "60–79% — recommended for review" },
  C: { label: "Priority C · Low Match", color: "#EF4444", desc: "below 60% — not recommended" },
  unranked: { label: "Unranked", color: "#94A3B8", desc: "AI screening or IVR not yet run" },
};

const REC_STYLE = {
  strong_hire: "bg-emerald-600 text-white",
  hire: "bg-emerald-100 text-emerald-800",
  review: "bg-amber-100 text-amber-800",
  not_recommended: "bg-red-100 text-red-700",
  recommend_hr_round: "bg-emerald-100 text-emerald-800",
  hold_for_review: "bg-amber-100 text-amber-800",
  strong_yes: "bg-emerald-600 text-white",
  yes: "bg-emerald-100 text-emerald-800",
  maybe: "bg-amber-100 text-amber-800",
  no: "bg-red-100 text-red-700",
};

export default function RankedShortlist() {
  const [params, setParams] = useSearchParams();
  const [reqs, setReqs] = useState([]);
  const [selReq, setSelReq] = useState(params.get("requisition") || "");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [priorityFilter, setPriorityFilter] = useState("");
  const [ivrBusy, setIvrBusy] = useState("");

  useEffect(() => {
    api.get("/requisitions?status=open").then((r) => {
      setReqs(r.data);
      if (!selReq && r.data[0]) setSelReq(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!selReq) return;
    setBusy(true);
    setParams({ requisition: selReq }, { replace: true });
    api.get(`/candidates/ranking?requisition_id=${selReq}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setBusy(false));
  }, [selReq, setParams]);

  const runIvr = async (cid) => {
    setIvrBusy(cid);
    try {
      const { data: r } = await api.post("/ivr/trigger", { candidate_id: cid });
      toast.success(`AI IVR complete: ${r.overall_ivr_score}/100`);
      const { data: fresh } = await api.get(`/candidates/ranking?requisition_id=${selReq}`);
      setData(fresh);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setIvrBusy(""); }
  };

  const rows = useMemo(() => {
    if (!data) return [];
    return priorityFilter ? data.candidates.filter((c) => c.priority === priorityFilter) : data.candidates;
  }, [data, priorityFilter]);

  return (
    <div data-testid="ranked-shortlist-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#E05A47] font-bold">HR Candidate Dashboard</div>
          <h1 className="font-heading font-black text-3xl tracking-tight mt-1 flex items-center gap-3">
            <Trophy size={26} className="text-[#0A251D]" /> Prioritised Shortlist
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Candidates ranked by weighted score (profile match + IVR screening + configurable weights).
            Priority A/B/C buckets per PRD §7. Click a row to open the consolidated Final Assessment.
          </p>
        </div>
        <div className="flex flex-col items-end">
          <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground font-bold mb-1">Requisition</label>
          <select data-testid="ranking-req-select" value={selReq} onChange={(e) => setSelReq(e.target.value)}
            className="border px-3 py-2 text-sm bg-white min-w-[280px]">
            <option value="">Select…</option>
            {reqs.map((r) => <option key={r.id} value={r.id}>{r.title}</option>)}
          </select>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-5">
            {["A", "B", "C", "unranked"].map((k) => (
              <button key={k} data-testid={`priority-tile-${k}`}
                onClick={() => setPriorityFilter(priorityFilter === k ? "" : k)}
                className={`text-left border rounded-xl p-4 transition-all duration-150 ${priorityFilter === k ? "border-[#0A251D] shadow-sm" : "border-border hover:border-[#0A251D]/40"}`}>
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: PRIORITY_META[k].color }} />
                  <div className="font-heading font-black text-3xl">{data.counts[k] || 0}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest font-bold mt-1">{PRIORITY_META[k].label}</div>
                <div className="text-xs text-muted-foreground mt-0.5">{PRIORITY_META[k].desc}</div>
              </button>
            ))}
            <div className="border rounded-xl p-4 bg-[#0A251D] text-white">
              <div className="flex items-center gap-2"><Users size={16} /><div className="font-heading font-black text-3xl">{data.counts.total}</div></div>
              <div className="text-[10px] uppercase tracking-widest font-bold mt-1">Total in Pipeline</div>
              <div className="text-xs text-white/70 mt-0.5">{data.requisition.openings} opening{data.requisition.openings > 1 ? "s" : ""}</div>
            </div>
          </div>

          <div className="border bg-white rounded-xl p-4 mt-4 flex items-center gap-4 flex-wrap" data-testid="weights-strip">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground"><Target size={13} /> Active scoring weights</div>
            {Object.entries(data.weights).map(([k, v]) => (
              <div key={k} className="text-xs"><span className="text-muted-foreground capitalize">{k.replace(/_/g, " ")}:</span> <span className="font-bold text-[#0A251D]">{v}%</span></div>
            ))}
            <Link to={`/requisitions/${data.requisition.id}`} className="ml-auto text-xs font-bold text-[#E05A47] hover:underline">Adjust weights →</Link>
          </div>

          {busy && <div className="text-sm text-muted-foreground mt-4">Loading…</div>}

          <div className="border bg-white rounded-xl overflow-x-auto mt-4" data-testid="ranking-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-secondary/50 text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
                  <th className="px-4 py-3">Priority</th>
                  <th className="px-4 py-3">Candidate</th>
                  <th className="px-4 py-3 text-center">Profile <br />match</th>
                  <th className="px-4 py-3 text-center">IVR <br />score</th>
                  <th className="px-4 py-3 text-center flex items-center gap-1 justify-center"><ArrowUpDown size={11} /> Weighted</th>
                  <th className="px-4 py-3">Recommendation</th>
                  <th className="px-4 py-3">CTC / Notice</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} className="border-b last:border-0 hover:bg-secondary/30" data-testid={`rank-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-xs font-bold">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: PRIORITY_META[c.priority].color }} />
                        {c.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold">{c.name}</div>
                      <div className="text-xs text-muted-foreground">{c.current_company || "—"} · {c.experience_years} yrs · {c.location || "—"} · via {c.source}</div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {c.profile_match ? <span className="font-bold text-[#0A251D]">{c.profile_match}</span> : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {c.ivr_score ? <span className="font-bold text-[#0A251D]">{c.ivr_score}</span> :
                        <button data-testid={`quick-ivr-${c.id}`} onClick={() => runIvr(c.id)} disabled={!!ivrBusy}
                          className="inline-flex items-center gap-1 text-[10px] font-bold text-[#E05A47] hover:underline disabled:opacity-40">
                          <Phone size={10} /> {ivrBusy === c.id ? "Calling…" : "Run"}
                        </button>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="font-heading font-black text-lg" style={{ color: PRIORITY_META[c.priority].color }}>{c.weighted_score}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-bold ${REC_STYLE[c.final_recommendation] || "bg-secondary"}`}>
                        {c.final_recommendation?.replace(/_/g, " ")}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      <div>{c.expected_ctc || "—"}</div>
                      <div className="text-muted-foreground">{c.notice_period || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link to={`/candidates/${c.id}`} data-testid={`view-cand-${c.id}`}
                        className="inline-flex items-center gap-1 text-xs font-bold text-[#0A251D] hover:underline">
                        <Eye size={11} /> Open
                      </Link>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    {priorityFilter ? `No Priority ${priorityFilter} candidates yet.` : "No candidates in this requisition yet."}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      {!data && !busy && <div className="text-sm text-muted-foreground mt-6">Pick a requisition to see the prioritised shortlist.</div>}
    </div>
  );
}
